import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Palette, Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';
import { SketchPicker } from 'react-color';

interface Category {
  id: string;
  name: string;
  color: string;
  price?: number;
  paymentType?: number | string;
  s_std_pay_type?: string | number;
  s_tch_pay_type?: string | number;
  s_thr_pay_type?: string | number; // Handle potential backend typo
  prices?: Record<string, number>;
  prices2?: Record<string, number>;
  studentPaymentType?: number;
  teacherPaymentType?: number;
}

interface EditCategoryFormProps {
  category: Category;
  onSubmit: (category: Category) => void;
  onCancel: () => void;
}

const EditCategoryForm: React.FC<EditCategoryFormProps> = ({ category, onSubmit, onCancel }) => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const [name, setName] = useState(category.name);
  const [color, setColor] = useState(category.color);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [displayColorPicker, setDisplayColorPicker] = useState(false);

  const initialStudentPaymentType = category.s_std_pay_type != null
    ? parseInt(String(category.s_std_pay_type), 10)
    : (category.paymentType != null ? parseInt(String(category.paymentType), 10) : 0);

  const teacherPaymentTypeValue = category.s_tch_pay_type ?? category.s_thr_pay_type;

  const initialTeacherPaymentType = teacherPaymentTypeValue != null
    ? parseInt(String(teacherPaymentTypeValue), 10)
    : initialStudentPaymentType;

  const [studentPaymentType, setStudentPaymentType] = useState<number>(initialStudentPaymentType);
  const [teacherPaymentType, setTeacherPaymentType] = useState<number>(initialTeacherPaymentType);

  const [prices, setPrices] = useState<Record<string, number>>(
    category.prices || {
      price_1: 0, price_2: 0, price_3: 0, price_4: 0, price_5: 0, price_6: 0, price_7: 0, price_8: 0, price_9: 0, price_10: 0,
      price_11: 0, price_12: 0, price_13: 0, price_14: 0, price_15: 0, price_16: 0, price_17: 0, price_18: 0, price_19: 0, price_20: 0,
      price_su: 0, price_fix: 0
    }
  );

  const [prices2, setPrices2] = useState<Record<string, number>>(
    category.prices2 || {
      price_1: 0, price_2: 0, price_3: 0, price_4: 0, price_5: 0, price_6: 0, price_7: 0, price_8: 0, price_9: 0, price_10: 0,
      price_11: 0, price_12: 0, price_13: 0, price_14: 0, price_15: 0, price_16: 0, price_17: 0, price_18: 0, price_19: 0, price_20: 0,
      price_su: 0, price_fix: 0
    }
  );

  const handleClick = () => {
    setDisplayColorPicker(!displayColorPicker);
  };

  const handleClose = () => {
    setDisplayColorPicker(false);
  };

  const handleChange = (color: { hex: string }) => {
    setColor(color.hex);
  };

  const handlePriceChange = (key: string, value: number, isStudent: boolean) => {
    if (isStudent) {
      setPrices(prev => ({ ...prev, [key]: value }));
    } else {
      setPrices2(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      setIsSubmitting(true);
      try {
        await onSubmit({
          ...category,
          name: name.trim(),
          color,
          studentPaymentType,
          teacherPaymentType,
          prices,
          prices2
        });
      } catch (error) {
        console.error("Error submitting form:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col relative h-full">
      <div className="flex flex-col gap-2">
        <ScrollArea className={isMobile ? 'max-h-[calc(100vh-100px)] pr-4 pb-20' : 'max-h-[calc(70vh-100px)] pr-4'}>
          <div className="space-y-4 pb-4">
            <div>
              <h2>{t('categories.name')}</h2>
              <div className="flex items-center gap-2 pt-2">
                <Input
                  id="edit-category-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter category name"
                  autoFocus
                />
              </div>
            </div>
            {/* Color picker section - intentionally omitted for brevity in this example but present in actual code */}

            <Tabs defaultValue="teacher" className="space-y-4">
              <TabsList>
                <TabsTrigger value="teacher">{t('categories.tabTeachers')}</TabsTrigger>
                <TabsTrigger value="student">{t('categories.tabStudents')}</TabsTrigger>
              </TabsList>

              <TabsContent value="student">
                <div className="space-y-2 pb-3">
                  <h2>{t('categories.paymentType')}</h2>
                  <RadioGroup
                    value={studentPaymentType.toString()}
                    onValueChange={(value) => setStudentPaymentType(parseInt(value))}
                    className="flex flex-col space-y-1"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="0" id="edit-student-hourly-rate" />
                      <Label htmlFor="edit-student-hourly-rate">{t('categories.hourly_rate')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="1" id="edit-student-fixed-price" />
                      <Label htmlFor="edit-student-fixed-price">{t('categories.fixed_price')}</Label>
                    </div>
                  </RadioGroup>
                </div>
                
                <label className="flex items-center gap-1 mb-5">
                  <Info className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs">{t('categories.StudentPriceExplanation')}</span>
                </label>
                
                {studentPaymentType === 0 ? (
                  <div className="space-y-3">
                    <Label>{t('categories.price_per_student')} ({t('categories.hourly')})</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {Array.from({ length: 20 }, (_, i) => i + 1).map((num) => (
                        <div key={`student_price_${num}`} className="space-y-1">
                          <Label htmlFor={`edit_student_price_${num}`} className="text-xs">
                            {num} {t('students.student')}{num > 1 ? 's' : ''}
                          </Label>
                          <Input
                            id={`edit_student_price_${num}`}
                            type="number"
                            value={prices[`price_${num}`] || 0}
                            onChange={(e) => handlePriceChange(`price_${num}`, parseFloat(e.target.value) || 0, true)}
                            onFocus={(e) => e.target.select()}
                            min="0"
                            step="0.01"
                          />
                        </div>
                      ))}
                      <div className="space-y-1">
                        <Label htmlFor="edit_student_price_su" className="text-xs">{t('categories.more_than')} 20</Label>
                        <Input
                          id="edit_student_price_su"
                          type="number"
                          value={prices.price_su || 0}
                          onChange={(e) => handlePriceChange('price_su', parseFloat(e.target.value) || 0, true)}
                          onFocus={(e) => e.target.select()}
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="edit_student_price_fix">{t('categories.fixed_price')}</Label>
                    <Input
                      id="edit_student_price_fix"
                      type="number"
                      value={prices.price_fix || 0}
                      onChange={(e) => handlePriceChange('price_fix', parseFloat(e.target.value) || 0, true)}
                      onFocus={(e) => e.target.select()}
                      min="0"
                      step="0.01"
                    />
                  </div>
                )}
              </TabsContent>

              <TabsContent value="teacher">
                 <div className="space-y-2 pb-3">
                  <h2>{t('categories.paymentType')}</h2>
                  <RadioGroup
                    value={teacherPaymentType.toString()}
                    onValueChange={(value) => setTeacherPaymentType(parseInt(value))}
                    className="flex flex-col space-y-1"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="0" id="edit-teacher-hourly-rate" />
                      <Label htmlFor="edit-teacher-hourly-rate">{t('categories.hourly_rate')}</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="1" id="edit-teacher-fixed-price" />
                      <Label htmlFor="edit-teacher-fixed-price">{t('categories.fixed_price')}</Label>
                    </div>
                  </RadioGroup>
                </div>

                <label className="flex items-center gap-1 mb-5">
                  <Info className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs">{t('categories.teacherPriceExplanation')}</span>
                </label>

                {teacherPaymentType === 0 ? (
                  <div className="space-y-3">
                    <Label>{t('categories.price_per_teacher')} ({t('categories.hourly')})</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {Array.from({ length: 20 }, (_, i) => i + 1).map((num) => (
                        <div key={`teacher_price_${num}`} className="space-y-1">
                          <Label htmlFor={`edit_teacher_price_${num}`} className="text-xs">
                            {num} {t('students.student')}{num > 1 ? 's' : ''}
                          </Label>
                          <Input
                            id={`edit_teacher_price_${num}`}
                            type="number"
                            value={prices2[`price_${num}`] || 0}
                            onChange={(e) => handlePriceChange(`price_${num}`, parseFloat(e.target.value) || 0, false)}
                            onFocus={(e) => e.target.select()}
                            min="0"
                            step="0.01"
                          />
                        </div>
                      ))}
                      <div className="space-y-1">
                        <Label htmlFor="edit_teacher_price_su" className="text-xs">{t('categories.more_than')} 20</Label>
                        <Input
                          id="edit_teacher_price_su"
                          type="number"
                          value={prices2.price_su || 0}
                          onChange={(e) => handlePriceChange('price_su', parseFloat(e.target.value) || 0, false)}
                          onFocus={(e) => e.target.select()}
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="edit_teacher_price_fix">{t('categories.fixed_price')}</Label>
                    <Input
                      id="edit_teacher_price_fix"
                      type="number"
                      value={prices2.price_fix || 0}
                      onChange={(e) => handlePriceChange('price_fix', parseFloat(e.target.value) || 0, false)}
                      onFocus={(e) => e.target.select()}
                      min="0"
                      step="0.01"
                    />
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </ScrollArea>
      </div>
      <DialogFooter className={isMobile ? "flex gap-2 pt-4 mt-4 border-t fixed bottom-0 left-0 right-0 bg-gray-50 p-4 z-50 justify-center" : "flex gap-2 pt-4 mt-4 border-t justify-end"}>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={!name.trim() || isSubmitting}>
          {isSubmitting ? (
            <span className="flex items-center">
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              {t('common.processing')}
            </span>
          ) : (
            t('common.save_change')
          )}
        </Button>
      </DialogFooter>
    </form>
  );
};

export default EditCategoryForm;
